import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { colors } from '../../brand-colors';

/**
 * Case Study Writer - Conversational AI Interview
 * Uses Claude to have a natural conversation that extracts the transformation story
 */
function CaseStudyWriter() {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Chat state
  const [caseStudyId, setCaseStudyId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [hasIP, setHasIP] = useState(false);
  const [frameworkNames, setFrameworkNames] = useState([]);
  const [readyToFinalize, setReadyToFinalize] = useState(false);

  // Finalized state
  const [isFinalized, setIsFinalized] = useState(false);
  const [generatedStory, setGeneratedStory] = useState(null);
  const [finalizedCaseStudy, setFinalizedCaseStudy] = useState(null);

  // Regeneration
  const [regenerating, setRegenerating] = useState(false);
  const [feedbackInput, setFeedbackInput] = useState('');
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);

  // Existing case studies
  const [existingCaseStudies, setExistingCaseStudies] = useState([]);
  const [viewingCaseStudy, setViewingCaseStudy] = useState(null);

  // IP Frameworks check
  const [frameworkCount, setFrameworkCount] = useState(0);

  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadExistingCaseStudies();
    checkForFrameworks();
  }, []);

  useEffect(() => {
    // Scroll to bottom when messages change
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadExistingCaseStudies = async () => {
    try {
      const response = await fetch('/api/authority-engineer/case-study/list', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setExistingCaseStudies(data.case_studies || []);
        }
      }
    } catch (err) {
      console.error('Error loading case studies:', err);
    } finally {
      setLoading(false);
    }
  };

  const checkForFrameworks = async () => {
    try {
      const response = await fetch('/api/authority-engineer/extract/frameworks', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const completedFrameworks = (data.frameworks || []).filter(f =>
            f.status === 'draft' || f.status === 'published'
          );
          setFrameworkCount(completedFrameworks.length);
        }
      }
    } catch (err) {
      console.error('Error checking frameworks:', err);
    }
  };

  const startChat = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/authority-engineer/case-study/chat/start', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();
      if (data.success) {
        setCaseStudyId(data.case_study_id);
        setMessages(data.messages || []);
        setHasIP(data.has_ip);
        setFrameworkNames(data.framework_names || []);
      } else {
        setError(data.error || 'Failed to start conversation');
      }
    } catch (err) {
      setError('Failed to start conversation');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || submitting) return;

    setSubmitting(true);
    setError('');
    const messageToSend = inputMessage.trim();
    setInputMessage('');

    // Optimistically add user message
    setMessages(prev => [...prev, { role: 'user', content: messageToSend }]);

    try {
      const response = await fetch('/api/authority-engineer/case-study/chat/message', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          case_study_id: caseStudyId,
          message: messageToSend
        })
      });

      const data = await response.json();
      if (data.success) {
        setMessages(data.messages);
        setReadyToFinalize(data.ready_to_finalize);
      } else {
        setError(data.error || 'Failed to send message');
        // Remove optimistic message on error
        setMessages(prev => prev.slice(0, -1));
      }
    } catch (err) {
      setError('Failed to send message');
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setSubmitting(false);
    }
  };

  const finalizeCaseStudy = async () => {
    setSubmitting(true);
    setError('');

    try {
      const response = await fetch('/api/authority-engineer/case-study/chat/finalize', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          case_study_id: caseStudyId,
          approved: true
        })
      });

      const data = await response.json();
      if (data.success) {
        setFinalizedCaseStudy(data.case_study);
        setGeneratedStory(data.case_study.story);
        setIsFinalized(true);
      } else {
        setError(data.error || 'Failed to finalize');
      }
    } catch (err) {
      setError('Failed to finalize case study');
    } finally {
      setSubmitting(false);
    }
  };

  const regenerateStory = async () => {
    setRegenerating(true);
    setError('');

    try {
      const response = await fetch(`/api/authority-engineer/case-study/${caseStudyId}/regenerate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ feedback: feedbackInput })
      });

      const data = await response.json();
      if (data.success) {
        setGeneratedStory(data.story);
        setFeedbackInput('');
        setShowFeedbackForm(false);
      } else {
        setError(data.error || 'Failed to regenerate');
      }
    } catch (err) {
      setError('Failed to regenerate story');
    } finally {
      setRegenerating(false);
    }
  };

  const abandonChat = async () => {
    if (!window.confirm('Are you sure you want to abandon this conversation? Your progress will be lost.')) {
      return;
    }

    try {
      await fetch('/api/authority-engineer/case-study/chat/abandon', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ case_study_id: caseStudyId })
      });
    } catch (err) {
      console.error('Error abandoning chat:', err);
    }

    // Reset state
    setCaseStudyId(null);
    setMessages([]);
    setInputMessage('');
    setReadyToFinalize(false);
    loadExistingCaseStudies();
  };

  const viewCaseStudy = async (csId) => {
    try {
      const response = await fetch(`/api/authority-engineer/case-study/${csId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setViewingCaseStudy(data.case_study);
        }
      }
    } catch (err) {
      console.error('Error loading case study:', err);
    }
  };

  const resetToStart = () => {
    setCaseStudyId(null);
    setMessages([]);
    setInputMessage('');
    setReadyToFinalize(false);
    setIsFinalized(false);
    setGeneratedStory(null);
    setFinalizedCaseStudy(null);
    setViewingCaseStudy(null);
    loadExistingCaseStudies();
  };

  // Loading
  if (loading) {
    return (
      <Container fluid className="py-4 px-4">
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="mt-2">Loading...</p>
        </div>
      </Container>
    );
  }

  // Viewing a completed case study
  if (viewingCaseStudy) {
    return (
      <Container fluid className="py-4 px-4">
        <div className="mb-4">
          <Button variant="link" className="p-0 mb-2" onClick={() => setViewingCaseStudy(null)}>
            &larr; Back to Case Studies
          </Button>
          <h1>{viewingCaseStudy.title}</h1>
          <p className="text-muted">
            {viewingCaseStudy.client_name} &bull; {viewingCaseStudy.industry}
          </p>
        </div>

        <Row>
          <Col lg={10}>
            <Card className="shadow mb-4">
              <Card.Body className="p-4">
                <ReactMarkdown>{viewingCaseStudy.story || 'No story generated yet.'}</ReactMarkdown>
              </Card.Body>
            </Card>

            <div className="d-flex gap-3">
              <Button
                variant="success"
                onClick={() => {
                  navigator.clipboard.writeText(viewingCaseStudy.story || '');
                  alert('Copied to clipboard!');
                }}
              >
                Copy to Clipboard
              </Button>
              <Button variant="outline-secondary" onClick={() => setViewingCaseStudy(null)}>
                Back to List
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  // Show finalized story
  if (isFinalized && generatedStory) {
    return (
      <Container fluid className="py-4 px-4">
        <div className="mb-4">
          <Link to="/command-center/authority-engineer" className="text-decoration-none mb-2 d-inline-block">
            &larr; Back to Authority Engineer
          </Link>
          <h1>Your Transformation Story</h1>
          {finalizedCaseStudy && (
            <p className="text-muted">
              {finalizedCaseStudy.client_name} &bull; {finalizedCaseStudy.industry}
            </p>
          )}
        </div>

        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

        <Row>
          <Col lg={10}>
            <Card className="shadow mb-4">
              <Card.Body className="p-4">
                <ReactMarkdown>{generatedStory}</ReactMarkdown>
              </Card.Body>
            </Card>

            {/* Feedback/Regeneration */}
            {showFeedbackForm ? (
              <Card className="shadow mb-4">
                <Card.Body>
                  <h5>What would you like to change?</h5>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={feedbackInput}
                    onChange={(e) => setFeedbackInput(e.target.value)}
                    placeholder="E.g., 'Make the unlock section more specific' or 'Focus more on the psychological aspects'"
                    className="mb-3"
                  />
                  <div className="d-flex gap-2">
                    <Button
                      variant="primary"
                      onClick={regenerateStory}
                      disabled={regenerating || !feedbackInput.trim()}
                    >
                      {regenerating ? <><Spinner size="sm" className="me-2" />Regenerating...</> : 'Regenerate'}
                    </Button>
                    <Button variant="outline-secondary" onClick={() => setShowFeedbackForm(false)}>
                      Cancel
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            ) : (
              <div className="d-flex gap-3 flex-wrap">
                <Button
                  variant="success"
                  onClick={() => {
                    navigator.clipboard.writeText(generatedStory);
                    alert('Story copied to clipboard!');
                  }}
                >
                  Copy to Clipboard
                </Button>
                <Button variant="outline-primary" onClick={() => setShowFeedbackForm(true)}>
                  Request Changes
                </Button>
                <Button variant="outline-dark" onClick={resetToStart}>
                  Write Another Case Study
                </Button>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    );
  }

  // Active chat interface
  if (caseStudyId && messages.length > 0) {
    return (
      <Container fluid className="py-4 px-4">
        <div className="mb-4">
          <Link to="/command-center/authority-engineer" className="text-decoration-none mb-2 d-inline-block">
            &larr; Back to Authority Engineer
          </Link>
          <h1>Case Study Interview</h1>
          {hasIP && frameworkNames.length > 0 && (
            <small className="text-success">
              Your IP frameworks: {frameworkNames.join(', ')}
            </small>
          )}
        </div>

        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

        <Row>
          <Col lg={8}>
            {/* Chat Messages */}
            <Card className="shadow mb-3" style={{ height: '60vh', display: 'flex', flexDirection: 'column' }}>
              <Card.Body style={{ overflowY: 'auto', flex: 1 }} className="p-4">
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`mb-3 ${msg.role === 'user' ? 'text-end' : ''}`}
                  >
                    <div
                      className={`d-inline-block p-3 rounded-3 ${
                        msg.role === 'user'
                          ? 'text-white'
                          : 'bg-light'
                      }`}
                      style={{
                        maxWidth: '85%',
                        backgroundColor: msg.role === 'user' ? colors.greyDarkest : undefined,
                        textAlign: 'left'
                      }}
                    >
                      {msg.role === 'assistant' ? (
                        <ReactMarkdown className="mb-0">{msg.content.replace('[READY_TO_SUMMARIZE]', '')}</ReactMarkdown>
                      ) : (
                        <p className="mb-0">{msg.content}</p>
                      )}
                    </div>
                    <small className="text-muted d-block mt-1">
                      {msg.role === 'user' ? 'You' : 'AI Interviewer'}
                    </small>
                  </div>
                ))}
                {submitting && (
                  <div className="mb-3">
                    <div className="d-inline-block p-3 rounded-3 bg-light">
                      <Spinner size="sm" className="me-2" />
                      <span className="text-muted">Thinking...</span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </Card.Body>

              {/* Input Area */}
              <Card.Footer className="bg-white p-3">
                <Form onSubmit={sendMessage}>
                  <div className="d-flex gap-2">
                    <Form.Control
                      type="text"
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      placeholder="Type your response..."
                      disabled={submitting}
                      style={{ fontSize: '1rem' }}
                    />
                    <Button
                      type="submit"
                      style={{ backgroundColor: colors.greyDarkest, border: 'none' }}
                      disabled={submitting || !inputMessage.trim()}
                    >
                      {submitting ? <Spinner size="sm" /> : 'Send'}
                    </Button>
                  </div>
                </Form>
              </Card.Footer>
            </Card>

            {/* Action Buttons */}
            <div className="d-flex justify-content-between">
              <Button variant="outline-secondary" size="sm" onClick={abandonChat}>
                Abandon & Start Over
              </Button>

              {readyToFinalize && (
                <Button
                  size="lg"
                  style={{ backgroundColor: '#28a745', border: 'none' }}
                  onClick={finalizeCaseStudy}
                  disabled={submitting}
                >
                  {submitting ? (
                    <><Spinner size="sm" className="me-2" />Generating Story...</>
                  ) : (
                    'Generate My Case Study'
                  )}
                </Button>
              )}
            </div>
          </Col>

          {/* Sidebar */}
          <Col lg={4}>
            <Card className="shadow mb-4">
              <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
                <h6 className="mb-0">How This Works</h6>
              </Card.Header>
              <Card.Body className="p-3">
                <p className="small text-muted mb-2">
                  Just have a natural conversation. The AI will ask follow-up questions to uncover:
                </p>
                <ul className="small text-muted mb-0">
                  <li>What the client thought vs. what you saw</li>
                  <li>The strategic domains at play</li>
                  <li>The key "unlock" that made it work</li>
                  <li>Results and lessons learned</li>
                </ul>
              </Card.Body>
            </Card>

            <Card className="shadow" style={{ backgroundColor: '#fff8e6', border: '1px solid #ffc107' }}>
              <Card.Body className="p-3">
                <h6 style={{ color: colors.goldDark }}>Tip</h6>
                <p className="small text-muted mb-0">
                  Don't just describe WHAT you did - share WHY it worked.
                  The AI will help you articulate the strategic thinking
                  that made the difference.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Start screen
  return (
    <Container fluid className="py-4 px-4">
      <div className="mb-4">
        <Link to="/command-center/authority-engineer" className="text-decoration-none mb-2 d-inline-block">
          &larr; Back to Authority Engineer
        </Link>
        <h1>Case Study Writer</h1>
        <p className="text-muted">Turn success stories into transformation stories that showcase your IP</p>
      </div>

      <Row>
        <Col lg={8}>
          {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

          {/* Recommendation if no IP frameworks */}
          {frameworkCount === 0 && (
            <Card className="shadow mb-4" style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107' }}>
              <Card.Body className="p-4">
                <h5 style={{ color: '#856404' }}>
                  Recommended: Extract Your IP First
                </h5>
                <p className="mb-3">
                  Case studies are most powerful when they showcase YOUR unique methodology.
                  We recommend extracting at least one IP framework first - then your case studies
                  will reference your specific approach, not just generic tactics.
                </p>
                <Link to="/command-center/authority-engineer/extract">
                  <Button variant="warning">
                    Extract Your First IP Framework &rarr;
                  </Button>
                </Link>
                <div className="mt-3">
                  <small className="text-muted">
                    Or continue below if you want to write a case study first.
                  </small>
                </div>
              </Card.Body>
            </Card>
          )}

          {frameworkCount > 0 && (
            <Alert variant="success" className="mb-4">
              <strong>You have {frameworkCount} IP framework{frameworkCount !== 1 ? 's' : ''} documented!</strong>{' '}
              Your case studies will reference your unique methodology.
            </Alert>
          )}

          <Card className="shadow mb-4">
            <Card.Body className="p-4">
              <h4 className="mb-3">A Conversation, Not a Form</h4>
              <p>
                Traditional case studies ask you to fill out rigid forms.
                This is different - it's a <strong>conversation</strong>.
              </p>
              <p>
                Just tell me about a recent win. I'll ask follow-up questions to uncover
                the REAL story - the strategic thinking that made the difference,
                the pattern you recognized that others missed, and the "unlock" that
                transformed your client's situation.
              </p>

              <div className="p-3 rounded mb-4" style={{ backgroundColor: '#f8f9fa' }}>
                <strong>What we'll explore together:</strong>
                <ul className="mb-0 mt-2">
                  <li>What the client thought the problem was vs. what you actually saw</li>
                  <li>The strategic domains at play (operations, psychology, economics...)</li>
                  <li>The ONE key insight - the "unlock" - that made everything work</li>
                  <li>Why it worked at a deeper level</li>
                  <li>What others should learn from this story</li>
                </ul>
              </div>

              <Button
                size="lg"
                style={{ backgroundColor: colors.greyDarkest, border: 'none' }}
                onClick={startChat}
              >
                Start Conversation &rarr;
              </Button>
            </Card.Body>
          </Card>

          {/* Existing Case Studies */}
          {existingCaseStudies.length > 0 && (
            <Card className="shadow">
              <Card.Header>
                <h5 className="mb-0">Your Case Studies</h5>
              </Card.Header>
              <Card.Body>
                {existingCaseStudies.map(cs => (
                  <div key={cs.id} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                    <div>
                      <strong>{cs.title}</strong>
                      <small className="text-muted d-block">
                        {cs.client_name} &bull; {cs.industry}
                      </small>
                    </div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => viewCaseStudy(cs.id)}
                    >
                      View
                    </Button>
                  </div>
                ))}
              </Card.Body>
            </Card>
          )}
        </Col>

        <Col lg={4}>
          <Card className="shadow" style={{ backgroundColor: '#e8f4f8' }}>
            <Card.Body className="p-4">
              <h5 className="mb-3" style={{ color: '#17a2b8' }}>Why This Approach Works</h5>
              <p className="small mb-2">
                Most case studies are <strong>execution reports</strong>: "We did X, got Y results."
                That's fine, but it just shows competence.
              </p>
              <p className="small mb-2">
                <strong>Transformation stories</strong> show what you SAW that others missed.
                They position you as someone who thinks differently.
              </p>
              <p className="small mb-0">
                When your case studies reference YOUR frameworks and methodology,
                prospects see you as an authority - not just a service provider.
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default CaseStudyWriter;
